import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { showErrorToast } from '../../utils/errorHandler';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const api = (await import('../../utils/api')).default;
      
      // Build login data with +260 prefix
      const cleanPhone = formData.email.replace(/\D/g, '');
      const loginData = {
        phone: `+260${cleanPhone}`,
        password: formData.password
      };
      
      const response = await api.post('/auth/login', loginData);

      const { token, user } = response.data;

      // Check if user is admin
      if (user.role !== 'admin') {
        toast({
          title: 'Access Denied',
          description: 'Admin access required',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Store admin token and user
      localStorage.setItem('tekstar_token', token);
      localStorage.setItem('tekstar_user', JSON.stringify(user));
      localStorage.setItem('is_admin', 'true');

      toast({
        title: 'Welcome back!',
        description: 'Logged in successfully as admin',
      });

      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      showErrorToast(toast, error, 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Portal</h1>
          <p className="text-gray-600">Tekstar Administration</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Admin Phone Number</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium" data-testid="phone-prefix">+260</span>
              <Input
                id="phone"
                type="tel"
                className="rounded-l-none"
                placeholder="XXX XXX XXX"
                maxLength={9}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                required
                data-testid="phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Authorized access only</p>
        </div>
      </Card>
    </div>
  );
};

export default AdminLogin;